import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
} from 'react'
import { fetchAppVersionService } from '@/services/appVersion/appVersionService'

type AppVersionContextType = {
    appVersion: string
    loading: boolean
    fetchAppVersion: () => Promise<void>
    refetchAppVersion: () => Promise<void>
}

const AppVersionContext = createContext<AppVersionContextType | undefined>(
    undefined,
)

export const AppVersionProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [appVersion, setAppVersion] = useState<string>('')
    const [loading, setLoading] = useState(false)

    const fetchAppVersion = useCallback(async () => {
        try {
            setLoading(true)
            const result = await fetchAppVersionService()
            const version =
                result?.responseContent?.[0]?.app_version || ''
            setAppVersion(version)
        } catch (error) {
            console.error('Failed to fetch app version', error)
        } finally {
            setLoading(false)
        }
    }, [])

    /**
     * 🔁 Same API – used after save/update
     */
    const refetchAppVersion = useCallback(async () => {
        await fetchAppVersion()
    }, [fetchAppVersion])

    /**
     * 🚀 Auto fetch once when provider mounts
     */
    useEffect(() => {
        fetchAppVersion()
    }, [fetchAppVersion])

    return (
        <AppVersionContext.Provider
            value={{
                appVersion,
                loading,
                fetchAppVersion,
                refetchAppVersion,
            }}
        >
            {children}
        </AppVersionContext.Provider>
    )
}

/**
 * ✅ Custom Hook
 */
export const useAppVersion = () => {
    const context = useContext(AppVersionContext)
    if (!context) {
        throw new Error(
            'useAppVersion must be used within AppVersionProvider',
        )
    }
    return context
}
